'use client';

/**
 * @fileoverview LineupSection — LOCI · CLT D4 Event Detail lineup list (FRE-755)
 *
 * Compact lineup surface that replaces the rich {@link ArtistSection} on the
 * mobile-first D4 event detail. Renders an `LINEUP` eyebrow + a vertical list
 * of artist rows: 56×56 gradient avatar, name in Space Grotesk, genre tags,
 * and a `FollowButton` on the right. Follow toggles are optimistic and roll
 * back visually on error; failures surface via the global `useNotification`
 * toast.
 *
 * PIP-146 — when an artist carries a real, attributed bio
 * (`description` + `descriptionAttribution`), the row renders an expandable
 * "About the artist" block below the genre tags, visually distinct from the
 * event blurb (`EventAboutSection`). The attribution footer (source credit +
 * link) is REQUIRED by CC BY-SA and always renders with an enriched bio.
 */

import React, { useCallback, useMemo, useState } from 'react';
import Image from 'next/image';
import { Eyebrow, FollowButton } from '@loci/ui';
import { useFollowArtistMutation, useUnfollowArtistMutation } from '@loci/api';
import { useNotification } from '@loci/store';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/atoms/Badge';
import { cn } from '@/components/atoms/primitives/utils';
import { ArtistBioAttribution } from '@/components/molecules/ArtistBioAttribution';
import { toDisplayImageUrl } from '@/lib/image-proxy/toDisplayImageUrl';
import type { ArtistBilling, EventDetailArtist, EventDetailCategory } from '@/types/eventDetail';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LineupSectionViewProps {
  artists: EventDetailArtist[];
  categories?: EventDetailCategory[];
  className?: string;
  /** Current follow state keyed by artistId; missing entries render as outline. */
  followingByArtistId?: Record<string, boolean>;
  /** Loading flag keyed by artistId; renders the FollowButton spinner. */
  loadingByArtistId?: Record<string, boolean>;
  onToggleFollow?: (artistId: string, nextFollowing: boolean) => void;
}

export type LineupSectionProps = Pick<
  LineupSectionViewProps,
  'artists' | 'categories' | 'className'
>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// 56×56 avatar — Tailwind `size-14` (3.5rem) matches the LOCI spec. Keeping
// the dimension as a named utility class keeps layout in the design-token
// system; only the category-derived gradient needs a dynamic value.
const AVATAR_SIZE_CLASS = 'size-14';
const AVATAR_IMAGE_SIZES = '56px';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

function getCategoryColor(categories?: EventDetailCategory[]): string {
  // WEB-1065: stays a hex literal on purpose — getAvatarGradient appends a hex
  // alpha suffix (`${color}99`), which a var() reference cannot take.
  return categories?.[0]?.color ?? '#6366F1';
}

function getAvatarGradient(categoryColor: string): string {
  return `linear-gradient(135deg, ${categoryColor} 0%, ${categoryColor}99 100%)`;
}

function getAvatarUrl(artist: EventDetailArtist): string | null {
  const raw =
    artist.imageUrls?.medium ??
    artist.imageUrls?.thumbnail ??
    artist.imageUrls?.large ??
    artist.imageUrl ??
    null;
  // Provider URL → same-origin proxy; unknown host → '' → null (gradient + initials).
  return toDisplayImageUrl(raw) || null;
}

function getDisplayGenres(artist: EventDetailArtist): string[] {
  if (artist.genres && artist.genres.length > 0) return artist.genres.slice(0, 3);
  if (artist.genre) return [artist.genre];
  return [];
}

/**
 * PIP-146 — characters of bio shown before the "Read more" toggle. Mirrors
 * the `ArtistSection` pattern (slice + ellipsis, full text on expand).
 */
const BIO_TRUNCATE_LENGTH = 160;

// API-1484 billing role → human label. Every role renders as the neutral DS
// Badge (a label badge; `cta`/`accent`/`solid` are reserved for Free, new, Sponsored).
const BILLING_LABEL: Record<ArtistBilling, string> = {
  HEADLINER: 'Headliner',
  SUPPORT: 'Support',
  OPENER: 'Opener',
};

/**
 * Stable sort by `performanceOrder` ASC with NULLS LAST. The API
 * (`GET /events/:id`) already returns artists in this order; we re-apply it
 * defensively so the UI contract holds even if a caller hands us an unsorted
 * array. `Array.prototype.sort` is stable in modern engines, so artists that
 * share an order (or are all `null`) keep their incoming relative position.
 */
function orderByPerformance(artists: EventDetailArtist[]): EventDetailArtist[] {
  return [...artists].sort((a, b) => {
    const aOrder = a.performanceOrder;
    const bOrder = b.performanceOrder;
    if (aOrder == null && bOrder == null) return 0;
    if (aOrder == null) return 1; // a sorts after b (NULLS LAST)
    if (bOrder == null) return -1; // b sorts after a (NULLS LAST)
    return aOrder - bOrder;
  });
}

// ---------------------------------------------------------------------------
// Artist bio (PIP-146)
// ---------------------------------------------------------------------------

interface LineupArtistBioProps {
  artist: EventDetailArtist;
}

/**
 * Truncate at the last word boundary before `maxLength` so the cut never
 * splits a word mid-way. Falls back to a hard slice when the text has no
 * usable space early enough.
 */
function truncateAtWordBoundary(text: string, maxLength: number): string {
  const hardSlice = text.slice(0, maxLength);
  const lastSpace = hardSlice.lastIndexOf(' ');
  const breakpoint = lastSpace > maxLength / 2 ? lastSpace : maxLength;
  return `${text.slice(0, breakpoint)}...`;
}

/**
 * Expandable "About the artist" block: real published bio text (verbatim from
 * the source) with a Read more toggle, followed by the CC BY-SA attribution
 * footer ({@link ArtistBioAttribution}) whenever the bio is enriched. Renders
 * nothing when the artist has no description.
 */
function LineupArtistBio({ artist }: LineupArtistBioProps) {
  const [expanded, setExpanded] = useState(false);

  const description = artist.description;
  if (!description || description.trim().length === 0) {
    return null;
  }

  const shouldTruncate = description.length > BIO_TRUNCATE_LENGTH;
  const displayBio =
    shouldTruncate && !expanded
      ? truncateAtWordBoundary(description, BIO_TRUNCATE_LENGTH)
      : description;

  return (
    <div className="mt-2" data-testid="lineup-artist-bio">
      <p className="text-xs text-muted-foreground leading-relaxed">{displayBio}</p>
      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-0.5 inline-flex min-h-11 desktop:min-h-0 items-center rounded-sm text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={expanded ? 'Show less' : 'Read more'}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
      <ArtistBioAttribution attribution={artist.descriptionAttribution} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface LineupRowProps {
  artist: EventDetailArtist;
  categoryColor: string;
  following: boolean;
  loading: boolean;
  onToggle: (artistId: string, nextFollowing: boolean) => void;
}

function LineupRow({ artist, categoryColor, following, loading, onToggle }: LineupRowProps) {
  const avatarUrl = getAvatarUrl(artist);
  const genres = getDisplayGenres(artist);

  return (
    <li
      data-testid="lineup-row"
      data-artist-id={artist.id}
      className="flex items-center gap-3 py-3 border-b border-border/50 last:border-b-0"
    >
      {avatarUrl ? (
        <div className={cn('relative shrink-0 overflow-hidden rounded-full', AVATAR_SIZE_CLASS)}>
          <Image src={avatarUrl} alt="" fill sizes={AVATAR_IMAGE_SIZES} className="object-cover" />
        </div>
      ) : (
        <div
          aria-hidden="true"
          data-testid="lineup-initials-avatar"
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
            AVATAR_SIZE_CLASS,
          )}
          // Dimensions/layout come from Tailwind; only the category-derived
          // gradient stays inline since it can't be expressed as a static
          // utility class.
          style={{ background: getAvatarGradient(categoryColor) }}
        >
          {getInitials(artist.name)}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-display text-sm font-semibold text-foreground truncate">
            {artist.name}
          </p>
          {artist.billing && (
            <Badge
              data-testid="lineup-billing-badge"
              data-billing={artist.billing}
              className="shrink-0 uppercase tracking-wide"
            >
              {BILLING_LABEL[artist.billing]}
            </Badge>
          )}
        </div>
        {genres.length > 0 && (
          <p className="mt-0.5 text-xs text-muted-foreground truncate">{genres.join(' · ')}</p>
        )}
        <LineupArtistBio artist={artist} />
      </div>

      <FollowButton
        following={following}
        loading={loading}
        onClick={() => onToggle(artist.id, !following)}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// View (pure)
// ---------------------------------------------------------------------------

export function LineupSectionView({
  artists,
  categories,
  className,
  followingByArtistId = {},
  loadingByArtistId = {},
  onToggleFollow,
}: LineupSectionViewProps) {
  // API already orders by performanceOrder ASC NULLS LAST; re-apply defensively
  // so the contract holds regardless of caller. Hooks must run unconditionally,
  // so memoize before the early return.
  const orderedArtists = useMemo(() => orderByPerformance(artists), [artists]);

  if (orderedArtists.length === 0) {
    return null;
  }

  const categoryColor = getCategoryColor(categories);

  return (
    <section aria-label="Lineup" data-testid="artist-section" className={cn('w-full', className)}>
      <div className="mb-4">
        <Eyebrow>LINEUP</Eyebrow>
      </div>
      <ul className="list-none">
        {orderedArtists.map((artist) => (
          <LineupRow
            key={artist.id}
            artist={artist}
            categoryColor={categoryColor}
            following={followingByArtistId[artist.id] ?? false}
            loading={loadingByArtistId[artist.id] ?? false}
            onToggle={(artistId, nextFollowing) => onToggleFollow?.(artistId, nextFollowing)}
          />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Connected
// ---------------------------------------------------------------------------

/**
 * Connected `LineupSection`. Owns optimistic follow state per artist and
 * wires the mutations to the `@loci/api` endpoints. Follow toggles
 * update visually immediately; on failure the state rolls back and a toast
 * surfaces the error. Anonymous viewers see the outline variant only; an
 * informational toast prompts sign-in on interaction.
 */
export function LineupSection({ artists, categories, className }: LineupSectionProps) {
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();
  const [followArtist] = useFollowArtistMutation();
  const [unfollowArtist] = useUnfollowArtistMutation();

  const [following, setFollowing] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const handleToggleFollow = useCallback(
    async (artistId: string, nextFollowing: boolean) => {
      if (!isAuthenticated) {
        showNotification({ type: 'info', title: 'Sign in to follow artists' });
        return;
      }

      // Optimistic: flip state immediately.
      setFollowing((prev) => ({ ...prev, [artistId]: nextFollowing }));
      setPending((prev) => ({ ...prev, [artistId]: true }));

      try {
        if (nextFollowing) {
          await followArtist({ artistId }).unwrap();
        } else {
          await unfollowArtist({ artistId }).unwrap();
        }
      } catch {
        // Rollback: revert to previous state and surface the failure.
        setFollowing((prev) => ({ ...prev, [artistId]: !nextFollowing }));
        showNotification({
          type: 'error',
          title: nextFollowing ? 'Failed to follow artist' : 'Failed to unfollow artist',
        });
      } finally {
        setPending((prev) => {
          const next = { ...prev };
          delete next[artistId];
          return next;
        });
      }
    },
    [isAuthenticated, showNotification, followArtist, unfollowArtist],
  );

  return (
    <LineupSectionView
      artists={artists}
      categories={categories}
      className={className}
      followingByArtistId={following}
      loadingByArtistId={pending}
      onToggleFollow={handleToggleFollow}
    />
  );
}
